import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AlertSubscriptionCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { AlertRuleCreate } from "../../../alertRule/components/create/AlertRuleCreate";
import { AlertRuleCreateStore } from "../../../alertRule/store";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AlertSubscriptionCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  alertRuleSelectedValue?: { displayId: string; id: string } | null;
  onAlertRuleSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onAlertRuleChange?: (value: string) => void;
  alertRuleOptions?: { displayId: string; id: string }[];
  alertRuleError?: string;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabled: boolean;
  enabledError?: string;
  alertSubscriptionCreateStore: AlertSubscriptionCreateStore;
};

export const AlertSubscriptionCreateForm: React.FC<AlertSubscriptionCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onAlertRuleSelected = () => {},
      alertRuleSelectedValue = null,
      alertRuleOptions = [],
      alertRuleError,
      onAlertRuleChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      alertSubscriptionCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AlertSubscription"
              mode="create"
              values={{}}
              options={{ alertRule: alertRuleOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "alertRule":
                    onAlertRuleSelected?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={alertSubscriptionCreateStore.getCardErrors("null")}
              title={langStore.groupAlertSubscriptionLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="alertRule"
                  label={langStore.AlertSubscriptionAlertRuleLabel}
                  autoFocus
                  error={Boolean(alertRuleError)}
                  helperText={
                    alertRuleError ||
                    alertSubscriptionCreateStore.warnings["alertRule"]?.message
                  }
                  selectedValue={alertRuleSelectedValue}
                  onChange={(value: string) => onAlertRuleChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onAlertRuleSelected(value)
                  }
                  options={alertRuleOptions}
                  canCreate={rootStore.authStore.hasPermissions(
                    "alertRule_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new AlertRuleCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <AlertRuleCreate
                          alertRuleCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewAlertRule"
                      cancelMethod="cancelNewAlertRule"
                    />
                  )}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    alertSubscriptionCreateStore.warnings["enabled"]?.message
                  }
                  label={langStore.AlertSubscriptionEnabledLabel}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
